import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react'

const JoystickControl = ({
  isConnected,
  sendCarCommand,
  currentDirection,
  isMoving
}) => {
  const [joystickPosition, setJoystickPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const joystickRef = useRef(null)
  const knobRef = useRef(null)

  const joystickSize = 192 // 12rem = 192px
  const knobSize = 64 // 4rem = 64px
  const maxDistance = (joystickSize - knobSize) / 2

  const handleMouseDown = (e) => {
    if (!isConnected) return
    setIsDragging(true)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleTouchStart = (e) => {
    if (!isConnected) return
    setIsDragging(true)
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)
  }

  const handleMouseMove = (e) => {
    if (!isDragging || !joystickRef.current) return
    
    const rect = joystickRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    
    const deltaX = e.clientX - centerX
    const deltaY = e.clientY - centerY
    
    updateJoystickPosition(deltaX, deltaY)
  }

  const handleTouchMove = (e) => {
    if (!isDragging || !joystickRef.current) return
    e.preventDefault()
    
    const rect = joystickRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    
    const touch = e.touches[0]
    const deltaX = touch.clientX - centerX
    const deltaY = touch.clientY - centerY
    
    updateJoystickPosition(deltaX, deltaY)
  }

  const updateJoystickPosition = (deltaX, deltaY) => {
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    
    if (distance > maxDistance) {
      const angle = Math.atan2(deltaY, deltaX)
      const clampedX = Math.cos(angle) * maxDistance
      const clampedY = Math.sin(angle) * maxDistance
      setJoystickPosition({ x: clampedX, y: clampedY })
    } else {
      setJoystickPosition({ x: deltaX, y: deltaY })
    }
    
    // Send command based on position
    sendCommandFromPosition(deltaX, deltaY)
  }

  const sendCommandFromPosition = (x, y) => {
    const normalizedX = x / maxDistance
    const normalizedY = -y / maxDistance // Invert Y axis
    
    const threshold = 0.3
    
    if (Math.abs(normalizedX) < threshold && Math.abs(normalizedY) < threshold) {
      // Center position - stop
      sendCarCommand('car_stop')
      return
    }
    
    // Determine direction based on position
    if (normalizedY > threshold && Math.abs(normalizedX) < threshold) {
      // Forward
      sendCarCommand('car_forward')
    } else if (normalizedY < -threshold && Math.abs(normalizedX) < threshold) {
      // Backward
      sendCarCommand('car_backward')
    } else if (normalizedX < -threshold && Math.abs(normalizedY) < threshold) {
      // Left
      sendCarCommand('car_left')
    } else if (normalizedX > threshold && Math.abs(normalizedY) < threshold) {
      // Right
      sendCarCommand('car_right')
    } else if (normalizedY > threshold && normalizedX < -threshold) {
      // Left-Forward
      sendCarCommand('car_left_forward')
    } else if (normalizedY > threshold && normalizedX > threshold) {
      // Right-Forward
      sendCarCommand('car_right_forward')
    } else if (normalizedY < -threshold && normalizedX < -threshold) {
      // Left-Backward
      sendCarCommand('car_left_backward')
    } else if (normalizedY < -threshold && normalizedX > threshold) {
      // Right-Backward
      sendCarCommand('car_right_backward')
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    setJoystickPosition({ x: 0, y: 0 })
    sendCarCommand('car_stop')
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
    setJoystickPosition({ x: 0, y: 0 })
    sendCarCommand('car_stop')
    document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTouchEnd)
  }

  // Cleanup event listeners
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [])

  const getDirectionIndicator = () => {
    const { x, y } = joystickPosition
    const normalizedX = x / maxDistance
    const normalizedY = -y / maxDistance
    
    if (Math.abs(normalizedX) < 0.3 && Math.abs(normalizedY) < 0.3) {
      return null
    }
    
    if (normalizedY > 0.3 && Math.abs(normalizedX) < 0.3) {
      return <ArrowUp className="w-6 h-6 text-primary-400" />
    } else if (normalizedY < -0.3 && Math.abs(normalizedX) < 0.3) {
      return <ArrowDown className="w-6 h-6 text-primary-400" />
    } else if (normalizedX < -0.3 && Math.abs(normalizedY) < 0.3) {
      return <ArrowLeft className="w-6 h-6 text-primary-400" />
    } else if (normalizedX > 0.3 && Math.abs(normalizedY) < 0.3) {
      return <ArrowRight className="w-6 h-6 text-primary-400" />
    } else if (normalizedY > 0.3 && normalizedX < -0.3) {
      return <div className="flex space-x-1"><ArrowUp className="w-4 h-4" /><ArrowLeft className="w-4 h-4" /></div>
    } else if (normalizedY > 0.3 && normalizedX > 0.3) {
      return <div className="flex space-x-1"><ArrowUp className="w-4 h-4" /><ArrowRight className="w-4 h-4" /></div>
    } else if (normalizedY < -0.3 && normalizedX < -0.3) {
      return <div className="flex space-x-1"><ArrowDown className="w-4 h-4" /><ArrowLeft className="w-4 h-4" /></div>
    } else if (normalizedY < -0.3 && normalizedX > 0.3) {
      return <div className="flex space-x-1"><ArrowDown className="w-4 h-4" /><ArrowRight className="w-4 h-4" /></div>
    }
    
    return null
  }

  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Joystick Control</h3>
        <p className="text-sm text-gray-400">
          {isConnected ? 'Drag the joystick to control the car' : 'Connect to a device first'}
        </p>
      </div>

      {/* Joystick */}
      <div className="relative">
        <div
          ref={joystickRef}
          className="joystick-area"
          style={{ width: joystickSize, height: joystickSize }}
        >
          {/* Center dot */}
          <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-white/30 rounded-full transform -translate-x-1/2 -translate-y-1/2" />
          
          {/* Direction indicators */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
            <ArrowUp className="w-4 h-4 text-white/20" />
          </div>
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
            <ArrowDown className="w-4 h-4 text-white/20" />
          </div>
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
            <ArrowLeft className="w-4 h-4 text-white/20" />
          </div>
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <ArrowRight className="w-4 h-4 text-white/20" />
          </div>
          
          {/* Joystick knob */}
          <motion.div
            ref={knobRef}
            className="joystick-knob"
            style={{
              width: knobSize,
              height: knobSize,
              left: joystickSize / 2 + joystickPosition.x - knobSize / 2,
              top: joystickSize / 2 + joystickPosition.y - knobSize / 2,
              cursor: isConnected ? 'grab' : 'not-allowed'
            }}
            animate={{
              scale: isDragging ? 1.1 : 1,
              boxShadow: isDragging 
                ? '0 0 20px rgba(59, 130, 246, 0.5)' 
                : '0 4px 8px rgba(0, 0, 0, 0.3)'
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {getDirectionIndicator()}
          </motion.div>
        </div>
      </div>

      {/* Status */}
      <div className="text-center">
        <div className="text-sm text-gray-400 mb-1">Current Direction</div>
        <div className="text-lg font-semibold text-primary-400">
          {currentDirection || 'STOP'}
        </div>
        {isMoving && (
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="text-xs text-success-400 mt-1"
          >
            Moving
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default JoystickControl 